import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { requestPasswordReset } from '../services/auth.service';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'required').email('invalid'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      await requestPasswordReset(data.email);
      setSuccessMessage(t('pages.forgotPassword.success'));
    } catch {
      setError(t('pages.forgotPassword.errors.submit'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
            <KeyRound className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('pages.forgotPassword.title')}</CardTitle>
          <CardDescription>{t('pages.forgotPassword.subtitle')}</CardDescription>
        </CardHeader>

        <form onSubmit={handleFormSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            {error && <Alert variant="error">{error}</Alert>}

            <div className="space-y-2">
              <Label htmlFor="email">{t('pages.forgotPassword.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                {...register('email')}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-red-500">
                  {errors.email.type === 'too_small'
                    ? t('pages.login.errors.emailRequired')
                    : t('pages.login.errors.emailInvalid')}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('pages.forgotPassword.submit')}
            </Button>
            <Link to="/login" className="text-sm text-orange-600 hover:underline">
              {t('pages.forgotPassword.backToLogin')}
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ForgotPassword;
